import { create } from 'zustand';

import { getView, type PrismTool } from '../engine/prism.js';
import { clearOverlay, setOverlay } from '../engine/host.js';
import { docIO, SAVE_DELAY_MS } from '../lib/doc-io.js';
import {
  applySceneCommand,
  copyEntities,
  entityExists,
  parseScene,
  resolveIds,
  serializeScene,
  type CommandResult,
  type EditorCommand,
  type EntityDesc,
  type SceneDoc,
} from '../lib/scene-doc.js';
import { logEditor } from './console.js';
import { usePlay } from './play.js';
import { useWorkspace } from './workspace.js';

/**
 * The warm Play engine (PlayTab) keeps the scene loaded in edit mode. Scene
 * edits are mirrored to it as they happen; edits made during a run are
 * caught up when the run stops ({@link syncPlayAfterRun}).
 */
let playStale = false;

function mirrorToPlay(command: EditorCommand | null, path: string | null): void {
  const view = getView('play');
  if (!view || !path) return;
  if (usePlay.getState().state !== 'stopped') {
    playStale = true;
    return;
  }
  if (!command) {
    void view.fileChanged(path);
    return;
  }
  void view.command(command).then((outcome) => {
    // Not loaded there yet, or the command did not apply: re-read the document.
    if (!outcome.ok) void view.fileChanged(path);
  });
}

/** After a run stops: re-read the scene if it was edited while playing. */
export function syncPlayAfterRun(): void {
  if (!playStale) return;
  playStale = false;
  const path = useScene.getState().path;
  if (path) void getView('play')?.fileChanged(path);
}

/**
 * The open scene document.
 *
 * Edits are applied locally with the same semantics as prism's EditorCommand,
 * forwarded to the live engine view when there is one, and saved through the
 * file API after a short debounce. The engine reports gizmo edits back as
 * `sceneChanged`; files changed on disk (by the AI) are reloaded.
 */

export type SaveState = 'saved' | 'pending' | 'saving' | 'error';
export type TransformSpace = 'local' | 'world';

const HISTORY_LIMIT = 100;
/** Engine edits closer together than this collapse into one undo step. */
const ENGINE_COALESCE_MS = 600;

export interface SceneState {
  path: string | null;
  doc: SceneDoc | null;
  loading: boolean;
  /** Load/parse problem with the file on disk. */
  error: string | null;
  /** Last rejected command, shown inline. */
  commandError: string | null;
  selection: string[];
  tool: PrismTool;
  space: TransformSpace;
  snap: boolean;
  cameraSpeed: number;
  collapsed: string[];
  saveState: SaveState;
  past: SceneDoc[];
  future: SceneDoc[];

  open: (path: string) => Promise<void>;
  close: () => void;
  apply: (command: EditorCommand) => CommandResult | null;
  /** Several commands as one edit and one undo step (a single one is applied as is). */
  applyBatch: (commands: EditorCommand[]) => CommandResult | null;
  /** Copies the selection (with children) to the editor clipboard, which outlives the scene. */
  copy: () => number;
  /** Pastes the clipboard: at the root, or `asChild` under the active entity. */
  paste: (options?: { asChild?: boolean; keepWorld?: boolean }) => CommandResult | null;
  duplicateSelection: () => CommandResult | null;
  deleteSelection: () => CommandResult | null;
  select: (ids: string[]) => void;
  setTool: (tool: PrismTool) => void;
  setSpace: (space: TransformSpace) => void;
  setSnap: (snap: boolean) => void;
  setCameraSpeed: (speed: number) => void;
  toggleCollapsed: (id: string) => void;
  setEnvironment: (environment: Record<string, unknown>) => void;
  undo: () => void;
  redo: () => void;
  /** `sceneChanged` from the engine (gizmo drags etc.). */
  fromEngine: (path: string, doc: unknown) => void;
  /** `selection` from the engine. */
  selectionFromEngine: (ids: string[]) => void;
  /** A file changed on disk. */
  externalChange: (path: string) => Promise<void>;
  /** Save now if anything is pending. */
  flush: () => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
/** What we last read from or wrote to disk, per path: our own echoes are ignored. */
const lastDisk = new Map<string, string>();
let lastEngineEdit = 0;

/* Editor clipboard: entities from {@link copyEntities}, shared across scenes and reloads. */
const CLIPBOARD_KEY = 'prism.sceneClipboard';
let clipboard: EntityDesc[] = [];

export function sceneClipboard(): EntityDesc[] {
  if (clipboard.length === 0) {
    try {
      const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(CLIPBOARD_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) clipboard = parsed as EntityDesc[];
    } catch {
      // Unreadable storage: an empty clipboard.
    }
  }
  return clipboard;
}

function setClipboard(entities: EntityDesc[]): void {
  clipboard = entities;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(entities));
  } catch {
    // Storage full or blocked: the in-memory copy still works.
  }
}

/** Equal documents, numbers compared at f32 precision (the engine's echo). */
export function sameDoc(a: SceneDoc, b: SceneDoc): boolean {
  const key = (doc: SceneDoc) =>
    JSON.stringify(doc, (_k, v: unknown) => (typeof v === 'number' ? Number(v.toPrecision(6)) : v));
  return key(a) === key(b);
}

/** Selected ids whose ancestors are not selected (they bring their children). */
export function selectionRoots(doc: SceneDoc, selection: readonly string[]): string[] {
  const set = new Set(selection);
  const parentOf = new Map(doc.entities.map((e) => [e.id, e.parent ?? null]));
  return selection.filter((id) => {
    let p = parentOf.get(id) ?? null;
    for (let guard = 0; p && guard < doc.entities.length; guard += 1) {
      if (set.has(p)) return false;
      p = parentOf.get(p) ?? null;
    }
    return parentOf.has(id);
  });
}

function projectId(): number | null {
  return useWorkspace.getState().projectId;
}

export const useScene = create<SceneState>((set, get) => {
  /** Replace the document; the shared bookkeeping for every edit path. */
  function commit(doc: SceneDoc, options: { history?: boolean; save?: boolean } = {}): void {
    const { history = true, save = true } = options;
    const state = get();
    const prev = state.doc;
    set({
      doc,
      past: history && prev ? [...state.past, prev].slice(-HISTORY_LIMIT) : state.past,
      future: history ? [] : state.future,
      // Entities inside prefab instances stay selectable (edited as overrides).
      selection: state.selection.filter((id) => entityExists(doc, id)),
      commandError: null,
    });
    if (state.path) setOverlay(state.path, serializeScene(doc));
    if (save) scheduleSave();
  }

  function scheduleSave(): void {
    if (saveTimer) clearTimeout(saveTimer);
    set({ saveState: 'pending' });
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void get().flush();
    }, SAVE_DELAY_MS);
  }

  /** The engine re-reads the document through the overlay. */
  function resyncEngine(): void {
    const { path } = get();
    if (path) void getView('scene')?.fileChanged(path);
    mirrorToPlay(null, path);
  }

  return {
    path: null,
    doc: null,
    loading: false,
    error: null,
    commandError: null,
    selection: [],
    tool: 'move',
    space: 'world',
    snap: false,
    cameraSpeed: 1,
    collapsed: [],
    saveState: 'saved',
    past: [],
    future: [],

    open: async (path) => {
      const pid = projectId();
      if (!pid) return;
      await get().flush();
      set({ path, loading: true, error: null, commandError: null });
      try {
        const text = await docIO().read(pid, path);
        if (get().path !== path) return;
        const doc = parseScene(text);
        lastDisk.set(path, text);
        setOverlay(path, text);
        set({
          doc,
          loading: false,
          selection: [],
          past: [],
          future: [],
          collapsed: [],
          saveState: 'saved',
        });
        void getView('scene')?.openScene(path);
      } catch (error) {
        if (get().path !== path) return;
        set({
          doc: null,
          loading: false,
          error: `${path}: ${error instanceof Error ? error.message : String(error)}`,
        });
      }
    },

    close: () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path } = get();
      if (path) clearOverlay(path);
      set({
        path: null,
        doc: null,
        error: null,
        commandError: null,
        selection: [],
        past: [],
        future: [],
        saveState: 'saved',
      });
    },

    apply: (command) => {
      const { doc } = get();
      if (!doc) return null;
      if (command.cmd === 'select') {
        get().select(command.ids);
        return { ok: true, doc };
      }
      const result = applySceneCommand(doc, command);
      if (!result.ok) {
        set({ commandError: result.error });
        logEditor('warn', `${command.cmd}: ${result.error}`);
        return result;
      }
      commit(result.doc);

      // Send the engine the resolved ids so both sides agree.
      const forwarded: EditorCommand = result.resolved ?? resolveIds(command, result.created);
      const view = getView('scene');
      const path = get().path;
      if (view) {
        void view.command(forwarded).then((outcome) => {
          if (outcome.ok) {
            mirrorToPlay(forwarded, path);
            return;
          }
          logEditor('warn', `engine rejected ${command.cmd}: ${outcome.error ?? 'unknown error'}`);
          resyncEngine(); // also re-reads the document in the Play engine
        });
      } else mirrorToPlay(forwarded, path);

      if (result.createdIds?.length) get().select(result.createdIds);
      else if ((command.cmd === 'addEntity' || command.cmd === 'duplicate') && result.created) get().select([result.created]);
      return result;
    },

    applyBatch: (commands) => {
      if (commands.length === 0) return null;
      return get().apply(commands.length === 1 ? commands[0]! : { cmd: 'batch', commands });
    },

    copy: () => {
      const { doc, selection } = get();
      if (!doc || selection.length === 0) return 0;
      const entities = copyEntities(doc, selection);
      setClipboard(entities);
      return entities.length;
    },

    paste: (options = {}) => {
      const { doc, selection } = get();
      const entities = sceneClipboard();
      if (!doc || entities.length === 0) return null;
      const parent = options.asChild ? (selection.at(-1) ?? null) : null;
      return get().apply({ cmd: 'paste', entities, parent, keepWorld: options.keepWorld !== false });
    },

    duplicateSelection: () => {
      const { doc, selection } = get();
      if (!doc) return null;
      return get().applyBatch(selectionRoots(doc, selection).map((id) => ({ cmd: 'duplicate', id })));
    },

    deleteSelection: () => {
      const { doc, selection } = get();
      if (!doc) return null;
      return get().applyBatch(selectionRoots(doc, selection).map((id) => ({ cmd: 'deleteEntity', id })));
    },

    select: (ids) => {
      const { doc } = get();
      const known = doc ? ids.filter((id) => entityExists(doc, id)) : [];
      set({ selection: known });
      getView('scene')?.command({ cmd: 'select', ids: known });
    },

    setTool: (tool) => {
      set({ tool });
      getView('scene')?.setTool(tool);
    },
    setSpace: (space) => {
      set({ space });
      getView('scene')?.setOption('space', space);
    },
    setSnap: (snap) => {
      set({ snap });
      getView('scene')?.setOption('snap', String(snap));
    },
    setCameraSpeed: (cameraSpeed) => {
      set({ cameraSpeed });
      getView('scene')?.setOption('cameraSpeed', String(cameraSpeed));
    },

    toggleCollapsed: (id) =>
      set((state) => ({
        collapsed: state.collapsed.includes(id)
          ? state.collapsed.filter((c) => c !== id)
          : [...state.collapsed, id],
      })),

    setEnvironment: (environment) => {
      const { doc } = get();
      if (!doc) return;
      commit({ ...doc, environment });
      resyncEngine();
    },

    undo: () => {
      const { past, doc, future } = get();
      const prev = past.at(-1);
      if (!prev || !doc) return;
      set({ past: past.slice(0, -1), future: [doc, ...future].slice(0, HISTORY_LIMIT) });
      commit(prev, { history: false });
      resyncEngine();
    },

    redo: () => {
      const { past, doc, future } = get();
      const next = future[0];
      if (!next || !doc) return;
      set({ past: [...past, doc].slice(-HISTORY_LIMIT), future: future.slice(1) });
      commit(next, { history: false });
      resyncEngine();
    },

    fromEngine: (path, raw) => {
      const { path: current } = get();
      if (path !== current || !raw || typeof raw !== 'object') return;
      let doc: SceneDoc;
      try {
        doc = parseScene(JSON.stringify(raw));
      } catch {
        return;
      }
      // The engine echoes every forwarded edit back; only real changes
      // (gizmo drags, engine-side keys) become undo steps.
      const open = get().doc;
      if (open && sameDoc(open, doc)) return;
      const now = Date.now();
      const coalesce = now - lastEngineEdit < ENGINE_COALESCE_MS;
      lastEngineEdit = now;
      commit(doc, { history: !coalesce });
    },

    selectionFromEngine: (ids) => {
      const { doc } = get();
      const known = doc ? ids.filter((id) => entityExists(doc, id)) : [];
      set({ selection: known });
    },

    externalChange: async (path) => {
      const pid = projectId();
      const { path: current } = get();
      if (!pid || path !== current) return;
      let text: string;
      try {
        text = await docIO().read(pid, path);
      } catch {
        return;
      }
      if (get().path !== path) return;
      const doc = get().doc;
      if (text === lastDisk.get(path) || (doc && text === serializeScene(doc))) return;
      lastDisk.set(path, text);
      let parsed: SceneDoc;
      try {
        parsed = parseScene(text);
      } catch (error) {
        // Often a write in progress; the next change event brings the rest.
        set({ error: `${path}: ${error instanceof Error ? error.message : String(error)}` });
        return;
      }
      // The file on disk wins over a pending local save.
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      set({ error: null, saveState: 'saved' });
      commit(parsed, { save: false });
      setOverlay(path, text);
      resyncEngine();
    },

    flush: async () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path, doc, saveState } = get();
      const pid = projectId();
      if (!path || !doc || !pid || saveState === 'saved') return;
      const text = serializeScene(doc);
      if (text === lastDisk.get(path)) {
        set({ saveState: 'saved' });
        return;
      }
      set({ saveState: 'saving' });
      try {
        lastDisk.set(path, text);
        await docIO().write(pid, path, text);
        if (get().path === path && get().saveState === 'saving') set({ saveState: 'saved' });
      } catch (error) {
        lastDisk.delete(path);
        set({ saveState: 'error' });
        logEditor('error', `Could not save ${path}: ${error instanceof Error ? error.message : error}`);
      }
    },
  };
});
